import { Component, ElementRef, EventEmitter, Input, OnDestroy, OnInit, Output, ViewChild } from '@angular/core';
import {
  CtrlNavigationContext,
  CtrlNavigationEntry,
} from '../../shared/_models/models';
import { collapsedContextSwitchNavConfig, CtrlNavigationContextConfig } from '../_services/unruly-navigation.config';
import { cloneDeep, findIndex } from 'lodash';
import { fadeWidthInOutAnimation, heightChangeAnimation } from '../_services/layout.animations';
import { LayoutService } from '../_services/layout.service';
import { NavigationPermissions } from '../../shared/_models/models';
import { catchError, takeUntil } from 'rxjs/operators';
import { combineLatest, Observable, Subject } from 'rxjs';
import { LottieCacheService } from '../../shared/_services/lottie-cache.service';

@Component({
  selector: 'app-unruly-navigation',
  templateUrl: './unruly-navigation.component.html',
  styleUrls: ['./unruly-navigation.component.less'],
  animations: [fadeWidthInOutAnimation, heightChangeAnimation]
})
export class UnrulyNavigationComponent implements OnInit, OnDestroy {

  @ViewChild('collapsedContextSwitchWrapperEl') collapsedContextSwitchWrapperEl: ElementRef;
  @ViewChild('collapsedContextSwitchLabelEl') collapsedContextSwitchLabelEl: ElementRef;

  @Input() isCollapsed = false;
  @Output() itemAction: EventEmitter<{ actionId: string; payload?: { context?: CtrlNavigationContext } }> = new EventEmitter();

  currentContext: CtrlNavigationContext;
  currentContextIndex = 0;
  collapsedContextSwitchPosition: Partial<CSSStyleDeclaration> = {};
  collapsedContextSwitchNavConfig: CtrlNavigationEntry = null;
  filteredNavContextList$: Observable<CtrlNavigationContextConfig[]>;
  filteredNavContextList: CtrlNavigationContextConfig[] = [];

  private unsubscribe$ = new Subject<void>();

  constructor(
    private layoutService: LayoutService,
    private lottieCache: LottieCacheService
  ) { }

  ngOnInit() {
    this.initServiceSubscriptions();
  }

  initServiceSubscriptions() {
    combineLatest([
      this.layoutService.currentContext$,
      this.layoutService.isFeatureToggleLoading$,
    ]).pipe(takeUntil(this.unsubscribe$)).subscribe(([context, isFeatureToggleLoading]) => {
      if (this.currentContext !== context && !isFeatureToggleLoading) {
        this.currentContext = context;
        this.layoutService.updateFilteredNavContextList();
        this.collapsedContextSwitchNavConfig = this.layoutService.filterVisibleNavItems([collapsedContextSwitchNavConfig])[0];
        this.preloadNavigationAnimationsFromConfig(this.collapsedContextSwitchNavConfig);
      }
    });

    this.layoutService.navigationPermissions$.pipe(takeUntil(this.unsubscribe$))
      .subscribe(() => this.layoutService.updateFilteredNavContextList());

    this.layoutService.filteredNavContextList$.pipe(takeUntil(this.unsubscribe$)).subscribe(list => {
      this.filteredNavContextList = cloneDeep(list);
      this.currentContextIndex = findIndex(this.filteredNavContextList, ['key', this.currentContext]);
      this.filteredNavContextList[this.currentContextIndex]?.navItems.forEach(item => {
        if (!this.isCollapsed && item.children?.length && item.children.some(child => child.isSelected)) {
          item.showChildren = true;
        }
      });
    });
  }

  getCurrentContextTitle(sliceLength: number = null) {
    return this.filteredNavContextList[this.currentContextIndex]?.title.slice(0, sliceLength);
  }

  onContextSwitch(context: CtrlNavigationContext) {
    this.layoutService.updateStateCurrentContext(context, true);
  }

  isFeatureToggleLoading() {
    return this.layoutService.isFeatureToggleLoading;
  }

  onItemAction(event: { actionId: string; payload?: { context?: CtrlNavigationContext } }) {
    this.itemAction.emit(event);
  }

  navigateHome() {
    this.itemAction.emit({ actionId: `navigateHome_${this.layoutService.currentContext}` });
  }

  hasNavigationPermission(permission: keyof NavigationPermissions): boolean {
    return this.layoutService.hasNavigationPermission(permission);
  }

  ngOnDestroy() {
    this.unsubscribe$.next();
    this.unsubscribe$.complete();
  }

  private extractLottieFilesFromNavConfig(navConfig: CtrlNavigationEntry): string[] {
    const lottieFiles: string[] = [];
    if (navConfig?.lottieFile) {
      lottieFiles.push(`resources/20250805075334/assets/lottie/${navConfig.lottieFile}.json`);
    }
    return lottieFiles;
  }

  private preloadNavigationAnimationsFromConfig(navConfig: CtrlNavigationEntry) {
    if (!navConfig) {
      return;
    }
    const lottieFiles = this.extractLottieFilesFromNavConfig(navConfig);
    if (lottieFiles.length === 0) {
      return;
    }
    this.lottieCache.preloadAnimations(lottieFiles);
  }

}
